import React, { useState, useEffect } from 'react';
import { Header } from '../../../components/Header/Header';
import * as styles from './ShopMaintenance.module.css';
import { FULL_CATALOG } from './data/fullCatalog';

// Fix image generation
const generateImages = (folder: string, count: number, hasBase: boolean = true): string[] => {
    const images: string[] = [];
    if (hasBase) {
        images.push(`/${folder}/high-res-product.png`);
    }
    for (let i = 1; i <= count; i++) {
        images.push(`/${folder}/high-res-product%20(${i}).png`);
    }
    return images;
};

const ALL_IMAGES = [
    ...generateImages('ta_small', 76, true),
    ...generateImages('ta_macro', 24, true),
    ...generateImages('ta_micro', 24, true),
];

// Catalog Modal Component
const CatalogModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Catálogo Completo</h2>
                    <button className={styles.closeBtn} onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className={styles.searchBar}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="M21 21l-4.35-4.35"></path>
                    </svg>
                    <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className={styles.catalogGrid}>
                    {Object.entries(FULL_CATALOG).map(([category, items]) => {
                        const filteredItems = items.filter(item =>
                            item.toLowerCase().includes(searchTerm.toLowerCase())
                        );

                        if (filteredItems.length === 0) return null;

                        return (
                            <div key={category} className={styles.categoryColumn}>
                                <h3 className={styles.categoryTitle}>{category}</h3>
                                <ul className={styles.productList}>
                                    {filteredItems.map(item => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export const ShopMaintenance: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [translateX, setTranslateX] = useState(0);
    const [showCatalog, setShowCatalog] = useState(false);

    // Auto-play
    useEffect(() => {
        if (isDragging || showCatalog) return;
        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % ALL_IMAGES.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [isDragging, showCatalog]);

    const handleDragStart = (clientX: number) => {
        setIsDragging(true);
        setStartX(clientX);
        setTranslateX(0);
    };

    const handleDragMove = (clientX: number) => {
        if (!isDragging) return;
        const diff = clientX - startX;
        setTranslateX(diff);
    };

    const handleDragEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);
        const threshold = 50;

        if (translateX > threshold) {
            setCurrentIndex(prev => (prev - 1 + ALL_IMAGES.length) % ALL_IMAGES.length);
        } else if (translateX < -threshold) {
            setCurrentIndex(prev => (prev + 1) % ALL_IMAGES.length);
        }

        setTranslateX(0);
    };

    return (
        <div className={styles.pageContainer}>
            <Header />

            <div
                className={styles.fullScreenCarousel}
                onMouseDown={!showCatalog ? e => handleDragStart(e.clientX) : undefined}
                onMouseMove={!showCatalog ? e => handleDragMove(e.clientX) : undefined}
                onMouseUp={!showCatalog ? handleDragEnd : undefined}
                onMouseLeave={!showCatalog ? () => isDragging && handleDragEnd() : undefined}
                onTouchStart={!showCatalog ? e => handleDragStart(e.touches[0].clientX) : undefined}
                onTouchMove={!showCatalog ? e => handleDragMove(e.touches[0].clientX) : undefined}
                onTouchEnd={!showCatalog ? handleDragEnd : undefined}
            >
                <div className={styles.messageOverlay}>
                    <h1>SITE UNDER MAINTENANCE</h1>
                    <button
                        className={styles.catalogBtn}
                        onClick={() => setShowCatalog(true)}
                    >
                        Desglosar Catálogo
                        <svg width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <path d="M16 10a4 4 0 0 1-8 0" />
                        </svg>
                    </button>
                    <p>Browse our collection below</p>
                </div>

                <div
                    className={styles.track}
                    style={{
                        transform: `translate3d(calc(-${currentIndex * 100}% + ${translateX}px), 0, 0)`,
                        transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)'
                    }}
                >
                    {ALL_IMAGES.map((src, index) => (
                        <div key={index} className={styles.slide}>
                            <img
                                src={src}
                                alt={`Product`}
                                className={styles.productImg}
                                draggable={false}
                                loading={Math.abs(index - currentIndex) < 2 ? "eager" : "lazy"}
                            />
                        </div>
                    ))}
                </div>

                <div className={styles.indicators}>
                    <span>{currentIndex + 1} / {ALL_IMAGES.length}</span>
                </div>
            </div>

            {showCatalog && <CatalogModal onClose={() => setShowCatalog(false)} />}
        </div>
    );
};

export default ShopMaintenance;
